import * as React from 'react';

import {AiSparkleNewIcon} from 'shells/icon/svg/ai-sparkle-new-icon';
import {ChartLineConnectedRegularReportIcon} from 'shells/icon/svg/chart-line-connected-regular-report-icon';
import {SecondarySidebar} from 'shells/navigation/secondary-sidebar/secondary-sidebar';
import type {NavItem} from 'shells/navigation/types';
import {LEAD_REPORT_TOUR_SIDEBAR_ID} from 'shells/tours/tours';

import {
    LOSSES,
    NEW_LEADS,
    SALES,
    FORECAST,
    FUNNEL,
    CUSTOM,
    ACTIVITY,
    EMAIL,
    ATTRIBUTION,
    TASKS,
    ENGAGEMENT,
    CHATBOT,
    getReportIcon,
    SCHEDULER_REPORT,
} from '../report-constants';

import {Footer} from './footer';

// TODO (@ianvs): use the current tab if possible, not always default tab
const DEFAULT_TAB = 'details';

const REPORT_LINKS: NavItem[] = [
    ...(NutClientConfig.hasReportGeneratorFF
        ? [
              {
                  id: 'insightsOverview',
                  label: 'Overview',
                  to: '/reports/overview',
                  icon: AiSparkleNewIcon,
                  requiresUpgrade: false,
                  isLegacyIcon: false,
              },
          ]
        : []),
    {
        label: 'Leads',
        isTopHeader: true,
    },
    {
        id: 'leadsSales',
        label: 'Sales',
        to: `/reports/sales/${DEFAULT_TAB}`,
        activeMatch: /^\/reports\/sales/,
        icon: getReportIcon(SALES),
        requiresUpgrade: false,
        isLegacyIcon: false,
    },
    {
        id: 'leadsLosses',
        label: 'Losses',
        to: `/reports/losses/${DEFAULT_TAB}`,
        activeMatch: /^\/reports\/losses/,
        icon: getReportIcon(LOSSES),
        requiresUpgrade: !NutClientConfig.hasOnDemandReporting,
        isLegacyIcon: false,
    },
    {
        id: 'leadsNewLeads',
        label: 'New leads',
        to: `/reports/newleads/${DEFAULT_TAB}`,
        activeMatch: /^\/reports\/newleads/,
        icon: getReportIcon(NEW_LEADS),
        requiresUpgrade: false,
        isLegacyIcon: false,
    },
    {
        id: 'leadsAttribution',
        label: 'Attribution',
        to: `/reports/attribution/${DEFAULT_TAB}`,
        activeMatch: /^\/reports\/attribution/,
        icon: getReportIcon(ATTRIBUTION),
        requiresUpgrade: !NutClientConfig.hasAttributionReport,
        isLegacyIcon: false,
    },
    {
        id: 'leadsForecast',
        label: 'Forecast',
        to: `/reports/forecast/${DEFAULT_TAB}`,
        activeMatch: /^\/reports\/forecast/,
        icon: getReportIcon(FORECAST),
        requiresUpgrade: !NutClientConfig.hasOnDemandReporting,
        isLegacyIcon: false,
    },
    {
        id: 'leadsCustom',
        label: 'Custom',
        to: `/reports/custom/${DEFAULT_TAB}`,
        activeMatch: /^\/reports\/custom/,
        icon: getReportIcon(CUSTOM),
        requiresUpgrade: !NutClientConfig.hasOnDemandReporting,
        isLegacyIcon: false,
    },
    {
        label: 'Effort',
        isHeader: true,
    },
    {
        id: 'insightsActivity',
        label: 'Activity',
        to: '/reports/activity',
        icon: getReportIcon(ACTIVITY),
        requiresUpgrade: !NutClientConfig.hasActivityReport,
        isLegacyIcon: false,
    },
    {
        id: 'insightsEmail',
        label: 'Email',
        to: '/reports/email',
        icon: getReportIcon(EMAIL),
        requiresUpgrade: !NutClientConfig.hasEmailReport,
        isLegacyIcon: false,
    },
    {
        id: 'insightsTasks',
        label: 'Tasks',
        to: '/reports/tasks',
        icon: getReportIcon(TASKS),
        requiresUpgrade: !NutClientConfig.hasTaskReport,
        isLegacyIcon: false,
    },
    {
        label: 'Other',
        isHeader: true,
    },
    {
        id: 'insightsSnapshots',
        label: 'Snapshots',
        to: '/reports/snapshots',
        icon: ChartLineConnectedRegularReportIcon,
        requiresUpgrade: !NutClientConfig.hasOnDemandReporting,
        isLegacyIcon: false,
    },
    {
        id: 'insightsFunnel',
        label: 'Funnel',
        to: '/reports/funnel',
        icon: getReportIcon(FUNNEL),
        requiresUpgrade: !NutClientConfig.hasFunnelReport,
        isLegacyIcon: false,
    },
];

if (NutClientConfig.hasEngagementReportFF) {
    REPORT_LINKS.push({
        id: 'insightsEngagement',
        label: 'Engagement',
        to: '/reports/engagement',
        icon: getReportIcon(ENGAGEMENT),
        // TODO: Update this to use the correct feature gate
        requiresUpgrade: false,
        isLegacyIcon: false,
    });
}

if (NutClientConfig.hasChatbotReportFF) {
    REPORT_LINKS.push({
        id: 'insightsChatbot',
        label: 'Chatbot',
        to: '/reports/chatbot',
        icon: getReportIcon(CHATBOT),
        requiresUpgrade: false,
        isLegacyIcon: false,
    });
}

if (NutClientConfig.hasSchedulerMeetingReportFF) {
    REPORT_LINKS.push({
        id: 'insightsScheduler',
        label: 'Scheduler',
        to: '/reports/schedulerReport',
        icon: getReportIcon(SCHEDULER_REPORT),
        requiresUpgrade: !NutClientConfig.canUseScheduler,
        isLegacyIcon: false,
    });
}

type Props = {
    isSessionUserLoading?: boolean;
};

export function Sidebar(props: Props) {
    return (
        <SecondarySidebar
            navItems={REPORT_LINKS}
            footer={!NutClientConfig.hasNutshellLitePlan && <Footer />}
            isLoading={props.isSessionUserLoading}
            id={LEAD_REPORT_TOUR_SIDEBAR_ID}
        />
    );
}
